import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ScienceIcon from '@mui/icons-material/Science';
import { BRAND } from '../../theme/brandTheme';
import { formatPercentage } from '../../utils/statisticsUtils';
import { baseChartOptions, CHART_COLORS } from './chartSetup';

/** KPI summary chip above charts */
const KpiChip = ({ icon, label, value, color = BRAND.purple }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 2,
      border: `1px solid ${BRAND.borderLight}`,
      bgcolor: '#fff',
      height: '100%',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
      {icon}
      <Typography variant="caption" sx={{ color: BRAND.textMuted, fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
    <Typography variant="h5" sx={{ fontWeight: 700, color, fontFamily: BRAND.fontFamily }}>
      {value}
    </Typography>
  </Box>
);

/** Post-test result visualizations — bar, doughnut, density charts */
const ResultsCharts = ({ postTestResults, postTestSettings, onShowChart }) => {
  const [densityTab, setDensityTab] = useState(0);

  const allVariants = useMemo(() => {
    if (!postTestResults) return [];
    return [
      { ...postTestResults.control, isControl: true },
      ...postTestResults.variants.map((v) => ({ ...v, isControl: false })),
    ];
  }, [postTestResults]);

  const conversionBarData = useMemo(() => {
    const labels = allVariants.map((v) => v.name);
    const rates = allVariants.map((v) => +(v.conversionRate * 100).toFixed(3));
    const colors = allVariants.map((v, i) =>
      v.isControl ? CHART_COLORS.control : CHART_COLORS.variants[(i - 1) % CHART_COLORS.variants.length],
    );
    return {
      labels,
      datasets: [
        {
          label: 'Dönüşüm Oranı (%)',
          data: rates,
          backgroundColor: colors.map((c) => `${c}CC`),
          borderColor: colors,
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    };
  }, [allVariants]);

  const liftBarData = useMemo(() => {
    const variants = postTestResults?.variants || [];
    return {
      labels: variants.map((v) => v.name),
      datasets: [
        {
          label: 'Lift (%)',
          data: variants.map((v) => +v.liftPercentage.toFixed(2)),
          backgroundColor: variants.map((v) =>
            v.liftPercentage >= 0 ? `${BRAND.green}CC` : `${BRAND.redLink}CC`,
          ),
          borderColor: variants.map((v) => (v.liftPercentage >= 0 ? BRAND.green : BRAND.redLink)),
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    };
  }, [postTestResults]);

  const winnerDoughnutData = useMemo(() => {
    const probs = postTestResults?.overallWinnerProbabilities;
    if (!probs?.length) return null;
    return {
      labels: probs.map((p) => p.name),
      datasets: [
        {
          data: probs.map((p) => +(p.winProbability * 100).toFixed(2)),
          backgroundColor: probs.map((p, i) =>
            p.isControl ? CHART_COLORS.control : CHART_COLORS.variants[i % CHART_COLORS.variants.length],
          ),
          borderWidth: 0,
        },
      ],
    };
  }, [postTestResults]);

  const densityVariants = postTestResults?.variants?.filter((v) => v.enhancedBayesian?.densityData) || [];
  const activeDensity = densityVariants[densityTab];

  const densityLineData = useMemo(() => {
    if (!activeDensity?.enhancedBayesian?.densityData) return null;
    const { xValues, controlDensity, variantDensity } = activeDensity.enhancedBayesian.densityData;
    return {
      labels: xValues.map((x) => (x * 100).toFixed(1)),
      datasets: [
        {
          label: postTestResults.control.name,
          data: controlDensity,
          borderColor: CHART_COLORS.control,
          backgroundColor: `${CHART_COLORS.control}33`,
          fill: true,
          tension: 0.4,
          pointRadius: 0,
        },
        {
          label: activeDensity.name,
          data: variantDensity,
          borderColor: BRAND.green,
          backgroundColor: `${BRAND.green}33`,
          fill: true,
          tension: 0.4,
          pointRadius: 0,
        },
      ],
    };
  }, [activeDensity, postTestResults]);

  const bestVariant = useMemo(() => {
    const variants = postTestResults?.variants || [];
    if (!variants.length) return null;
    return variants.reduce((best, v) => (v.liftPercentage > best.liftPercentage ? v : best), variants[0]);
  }, [postTestResults]);

  const hasSignificantWinner = postTestResults?.variants?.some(
    (v) => v.statisticalTest?.isSignificant && v.liftPercentage > 0,
  );

  if (!postTestResults) return null;

  const barOptions = {
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      legend: { display: false },
      title: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { font: { family: BRAND.fontFamily } },
        grid: { color: 'rgba(0,0,0,0.06)' },
      },
      x: {
        ticks: { font: { family: BRAND.fontFamily } },
        grid: { display: false },
      },
    },
  };

  const doughnutOptions = {
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      legend: { position: 'bottom' },
    },
  };

  const lineOptions = {
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      title: { display: false },
    },
    scales: {
      x: {
        title: { display: true, text: 'Dönüşüm Oranı (%)', font: { family: BRAND.fontFamily } },
        ticks: { maxTicksLimit: 8, font: { family: BRAND.fontFamily } },
      },
      y: {
        title: { display: true, text: 'Olasılık Yoğunluğu', font: { family: BRAND.fontFamily } },
        grid: { color: 'rgba(0,0,0,0.06)' },
      },
    },
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.purple, fontFamily: BRAND.fontFamily }}>
            Görsel Analiz
          </Typography>
          <Chip
            size="small"
            label={postTestSettings.analysisMethod === 'bayesian' ? 'Bayesian' : postTestSettings.analysisMethod.toUpperCase()}
            sx={{ bgcolor: `${BRAND.purple}14`, color: BRAND.purple, fontWeight: 600 }}
          />
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiChip
              icon={<TrendingUpIcon sx={{ color: BRAND.green, fontSize: 20 }} />}
              label="En İyi Lift"
              value={bestVariant ? `${bestVariant.liftPercentage > 0 ? '+' : ''}${bestVariant.liftPercentage.toFixed(2)}%` : '—'}
              color={bestVariant?.liftPercentage >= 0 ? BRAND.green : BRAND.redLink}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiChip
              icon={<ScienceIcon sx={{ color: BRAND.purple, fontSize: 20 }} />}
              label="Kontrol CR"
              value={formatPercentage(postTestResults.control.conversionRate)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiChip
              icon={<EmojiEventsIcon sx={{ color: BRAND.green, fontSize: 20 }} />}
              label="Anlamlı Sonuç"
              value={hasSignificantWinner ? 'Var' : 'Yok'}
              color={hasSignificantWinner ? BRAND.green : BRAND.textMuted}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <KpiChip
              icon={<EmojiEventsIcon sx={{ color: CHART_COLORS.control, fontSize: 20 }} />}
              label="Varyant Sayısı"
              value={String(postTestResults.variants.length + 1)}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: BRAND.textGray }}>
              Dönüşüm Oranı Karşılaştırması
            </Typography>
            <Box sx={{ height: 280 }}>
              <Bar data={conversionBarData} options={barOptions} />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: BRAND.textGray }}>
              Kontrole Göre Lift
            </Typography>
            <Box sx={{ height: 280 }}>
              <Bar data={liftBarData} options={barOptions} />
            </Box>
          </Grid>

          {winnerDoughnutData && (
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: BRAND.textGray }}>
                Kazanma Olasılıkları
              </Typography>
              <Box sx={{ height: 280, display: 'flex', justifyContent: 'center' }}>
                <Doughnut data={winnerDoughnutData} options={doughnutOptions} />
              </Box>
            </Grid>
          )}

          {densityLineData && (
            <Grid item xs={12} md={winnerDoughnutData ? 7 : 12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: BRAND.textGray }}>
                  Olasılık Yoğunluğu Dağılımı
                </Typography>
                {densityVariants.length > 1 && (
                  <Tabs
                    value={densityTab}
                    onChange={(_, v) => setDensityTab(v)}
                    sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0, fontSize: 12 } }}
                  >
                    {densityVariants.map((v, i) => (
                      <Tab key={v.name} label={v.name} value={i} />
                    ))}
                  </Tabs>
                )}
              </Box>
              <Box
                sx={{ height: 280, cursor: onShowChart ? 'pointer' : 'default' }}
                onClick={() => onShowChart?.(activeDensity)}
                title={onShowChart ? 'Detaylı görünüm için tıklayın' : undefined}
              >
                <Line data={densityLineData} options={lineOptions} />
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ResultsCharts;
